import React, { useState, useEffect } from 'react';
import '../styles/ProgramPages.css';
import '../styles/benefits.css';
import '../styles/master.css';
import SEO from '../components/SEO';
import '../styles/participants.css';
import '../styles/learning-experience.css';
import '../styles/pricing.css';
import '../styles/faq.css';
import '../styles/cta-final.css';
import '../styles/PopupForm.css';
import '../styles/features.css';
import '../styles/confirmation-modal.css';
import programsData from '../data/programs.json';
import Footer from '../components/Footer';
import PopupForm from '../components/PopupForm';
import { API_ENDPOINTS } from '../config/constants';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';

function gtag_report_conversion(url) {
  if (window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: 'AW-16782075468',
      event_callback: function () {
        if (url) {
          window.location = url;
        }
      }
    });
    return false;
  }
  return true;
}

function GH300() {
  const data = programsData.gh300Certification;

  // Pricing by location from querystring, default to IN
  function getQueryParam(name) {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
  }
  const locationParam = getQueryParam('location') || 'IN';
  const pricingData = data && data.pricing && data.pricing.locations ? data.pricing.locations[locationParam] || data.pricing.locations['IN'] : null;
  let pricingCurrency = '', pricingAmount = '', pricingOriginalCurrency = '', pricingOriginalAmount = '';
  if (pricingData) {
    const match = pricingData.current.match(/^(\D+)?(\d+(?:\.\d+)?)/);
    if (match) {
      pricingCurrency = match[1] || '';
      pricingAmount = match[2];
    }
    const origMatch = pricingData.original.match(/^(\D+)?(\d+(?:\.\d+)?)/);
    if (origMatch) {
      pricingOriginalCurrency = origMatch[1] || '';
      pricingOriginalAmount = origMatch[2];
    }
  }

  // Feature highlights for GH-300
  const [featureIndex, setFeatureIndex] = useState(0);
  const featureTiles = [
    {
      icon: '/images/icons/handson.png',
      title: 'Hands-On First',
      desc: 'Start coding with Copilot from day one—no theory overload.'
    },
    {
      icon: '/images/icons/interactive.svg',
      title: 'Bring Your Own Scenario',
      desc: 'Apply learnings to your actual projects and use cases.'
    },
    {
      icon: '/images/icons/copilot/certificate.svg',
      title: 'Ready to Apply',
      desc: 'Practical skills you can use in production immediately.'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setFeatureIndex(i => (i === featureTiles.length - 1 ? 0 : i + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [featureTiles.length]);

  // Inline hero form state
  const [showInlineHeroForm, setShowInlineHeroForm] = useState(true);
  const [inlineName, setInlineName] = useState('');
  const [inlineEmail, setInlineEmail] = useState('');
  const [inlineHeroSubmitting, setInlineHeroSubmitting] = useState(false);
  const [inlineHeroSubmitted, setInlineHeroSubmitted] = useState(false);
  const [inlineHeroError, setInlineHeroError] = useState('');

  // Scroll-up chevron state
  const [showScrollUp, setShowScrollUp] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY || document.documentElement.scrollTop;
      const pageHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (pageHeight > 0 && scrolled > pageHeight * 0.10) {
        setShowScrollUp(true);
      } else {
        setShowScrollUp(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [openFaq, setOpenFaq] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [formType, setFormType] = useState('');
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    message: '',
    title: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openPopup = (type) => {
    setFormType(type);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const showConfirmation = (type) => {
    const messages = {
      enroll: {
        title: "Enrollment Successful!",
        message: "Thank you for enrolling in the GH-300 GitHub Copilot Certification course. We'll send you hands-on exercises and access details via email."
      },
      curriculum: {
        title: "Curriculum Sent!",
        message: "The course curriculum with all 7 domains has been sent to your email. Check your inbox shortly."
      }
    };
    
    setConfirmationModal({
      isOpen: true,
      title: messages[type].title,
      message: messages[type].message
    });

    setTimeout(() => {
      setConfirmationModal(prev => ({ ...prev, isOpen: false }));
    }, 3000);
  };

  const closeConfirmation = () => {
    setConfirmationModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleFormSubmit = async (formData) => {
    try {
      const response = await fetch(API_ENDPOINTS.LEADS_SUBMIT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          source: 'gh300_page',
          interestedIn: formType === 'enroll' ? 'gh300_certification' : 'gh300_info'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      closePopup();
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit form. Please try again.');
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "What is the GH-300 GitHub Copilot certification?",
      answer: "GH-300 is Microsoft's official certification that validates your ability to use GitHub Copilot to optimize software development workflows. It covers 7 domains including Responsible AI, Copilot features, prompt engineering, and privacy."
    },
    {
      question: "How is this different from other courses?",
      answer: "We follow Lead With Tech principles: Hands-On First (code from day one), Bring Your Own Scenario (apply to your projects), and Ready to Apply (use skills immediately in production)."
    },
    {
      question: "Do I need prior GitHub Copilot experience?",
      answer: "Basic coding experience is helpful, but not required. The course starts with fundamentals and progresses to advanced AI-assisted development techniques."
    },
    {
      question: "How long does it take to complete?",
      answer: "Most students complete the hands-on practice in 3-4 weeks. The exam itself is 100 minutes. You can go at your own pace."
    },
    {
      question: "What's included in the course?",
      answer: "Hands-on coding exercises for all 7 exam domains, real-world scenarios, practice assessments, exam sandbox access, and a study guide aligned with Microsoft's official curriculum."
    },
    {
      question: "Can I bring my own project scenarios?",
      answer: "Absolutely! We encourage you to apply learnings to your actual work projects. That's our 'Bring Your Own Scenario' principle in action."
    }
  ];

  const testimonials = require('../data/testimonials.json').filter(t => t.program === 'gh300');

  return (
    <div className="gh300-page" style={{ position: 'relative' }}>
      <SEO pageName="gh300Certification" />
      <section className="hero-section gh300-hero" style={{ position: 'relative', backgroundImage: 'url(/images/background.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', width: '100%' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', zIndex: 1 }}></div>
        <div className="hero-content responsive-hero-content" style={{ display: 'flex', alignItems: 'center', gap: '2.5rem', position: 'relative', zIndex: 2 }}>
          <div className="hero-text" style={{ flex: 1 }}>
            <h1>{data.hero.title}</h1>
            <p className="hero-subtitle">{data.hero.subtitle}</p>
            <div className="cta-buttons">
              {data.hero.ctaButtons.map((button, index) =>
                button.type === 'primary' ? null : (
                  <button
                    key={index}
                    onClick={() => { gtag_report_conversion(); openPopup('curriculum'); }}
                    className={`button button-${button.type}`}
                  >
                    {button.text}
                  </button>
                )
              )}
            </div>
            {showInlineHeroForm && (
              <div className="inline-hero-lead-form" style={{ marginTop: 18, borderRadius: 10, boxShadow: '0 2px 12px rgba(44,62,80,0.10)', padding: 18, maxWidth: 280 }}>
                <form
                  onSubmit={async e => {
                    e.preventDefault();
                    if (!inlineName.trim() || !inlineEmail.trim()) {
                      setInlineHeroError('Please enter your name and email.');
                      return;
                    }
                    setInlineHeroError('');
                    setInlineHeroSubmitting(true);
                    try {
                      let token = localStorage.getItem('token');
                      if (!token) {
                        token = generateToken();
                        localStorage.setItem('token', token);
                      }
                      const headers = {
                        'Content-Type': 'application/json',
                        [TOKEN_HEADER]: token
                      };
                      const response = await fetch(API_ENDPOINTS.LEADS_SUBMIT, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({
                          name: inlineName,
                          email: inlineEmail,
                          source: 'gh300_hero_inline',
                          interestedIn: 'gh300_copilot_certification'
                        })
                      });
                      if (!response.ok) throw new Error('Failed to submit');
                      setInlineHeroSubmitted(true);
                      setTimeout(() => {
                        setShowInlineHeroForm(false);
                        setInlineHeroSubmitted(false);
                        setInlineName('');
                        setInlineEmail('');
                      }, 2500);
                    } catch (e) {
                      setInlineHeroError('Failed to submit. Please try again.');
                    } finally {
                      setInlineHeroSubmitting(false);
                    }
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div className="inline-hero-form-row">
                      <input
                        type="text"
                        placeholder="Name"
                        value={inlineName}
                        onChange={e => setInlineName(e.target.value)}
                        disabled={inlineHeroSubmitting}
                        style={{ flex: 1, padding: 8, borderRadius: 5, border: '1px solid #ccc', minWidth: 120 }}
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={inlineEmail}
                        onChange={e => setInlineEmail(e.target.value)}
                        disabled={inlineHeroSubmitting}
                        style={{ flex: 1, padding: 8, borderRadius: 5, border: '1px solid #ccc', minWidth: 120 }}
                      />
                      <button
                        type="submit"
                        disabled={inlineHeroSubmitting}
                        style={{ background: '#DA291C', color: '#fff', border: 'none', borderRadius: 5, padding: '8px 18px', fontWeight: 600, fontSize: '1rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        {inlineHeroSubmitting ? 'Submitting...' : 'Start Learning'}
                      </button>
                    </div>
                    {inlineHeroError && <div style={{ color: '#DA291C', fontSize: '0.95rem' }}>{inlineHeroError}</div>}
                    {inlineHeroSubmitted && <div style={{ color: 'green', fontWeight: 600 }}>Thank you! We'll be in touch soon.</div>}
                  </div>
                </form>
              </div>
            )}
            <p className="stats-text" dangerouslySetInnerHTML={{ __html: data.hero.statsText }} />
          </div>
          <div className="hero-image responsive-hero-image" style={{ flex: '0 0 340px', textAlign: 'right' }}>
            <img 
              src="/images/GH300.svg" 
              alt="GitHub Copilot Certification"
              className="rounded-image"
              style={{ maxWidth: '340px', width: '100%', height: 'auto', borderRadius: '18px', boxShadow: '0 4px 24px rgba(44,62,80,0.10)' }}
            />
          </div>
        </div>
      </section>

      <section style={{ background: '#fff', padding: '48px 0 32px 0' }}>
        <h2 style={{ textAlign: 'center', fontWeight: 700, fontSize: '2.2rem', marginBottom: '0.5rem', color: '#222' }}>
          Master GitHub Copilot — <span style={{ color: '#DA291C' }}>The Lead With Tech Way</span>
        </h2>
        <p style={{ textAlign: 'center', fontSize: '1.15rem', color: '#444', marginBottom: '2.5rem' }}>
          Hands-On First · Bring Your Own Scenario · Ready to Apply
        </p>
        <div className="gh300-feature-slider" style={{ maxWidth: 400, margin: '0 auto' }}>
          <div
            style={{
              background: '#fff',
              borderRadius: '18px',
              boxShadow: '0 2px 12px rgba(44,62,80,0.08)',
              padding: '32px',
              minWidth: '220px',
              maxWidth: '360px',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              transition: 'all 0.3s',
            }}
          >
            <img src={featureTiles[featureIndex].icon} alt={featureTiles[featureIndex].title} style={{ width: '64px', height: '64px', marginBottom: '18px', display: 'block' }} />
            <h3 style={{ fontWeight: 700, fontSize: '1.25rem', marginBottom: '12px', color: '#222' }}>{featureTiles[featureIndex].title}</h3>
            <p style={{ color: '#444', fontSize: '1rem' }}>{featureTiles[featureIndex].desc}</p>
          </div>
        </div>
      </section>

      {testimonials.length > 0 && (
        <section className="testimonials-section">
          <div className="container">
            <h2 style={{ textAlign: 'center', color: '#DA291C', fontWeight: 700 }}>What Our Students Say</h2>
            <div className="testimonials-grid">
              {testimonials.map((t, i) => (
                <div
                  key={t.id}
                  className="testimonial-card fade-in"
                  style={{ animationDelay: `${i * 0.2}s` }}
                >
                  <p>{t.quote}</p>
                  <h4>{t.name}</h4>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="master-section">
        <div className="container">
          <h2>7 Domains — <span className="highlight">Hands-On Mastery</span></h2>
          <p className="subtitle">Aligned with official GH-300 exam • Practice with real scenarios</p>
          
          <div className="master-grid">
            <div className="master-card">
              <div className="master-icon">
                <img src="/images/icons/interactive.svg" alt="Responsible AI" />
              </div>
              <div className="master-content">
                <h3>Domain 1: Responsible AI (7%)</h3>
                <p>Build AI-powered solutions ethically. Practice identifying bias, ensuring transparency, and implementing responsible AI practices in real code.</p>
              </div>
            </div>
            
            <div className="master-card">
              <div className="master-icon">
                <img src="/images/icons/copilot/certificate.svg" alt="Copilot Plans" />
              </div>
              <div className="master-content">
                <h3>Domain 2: Copilot Plans & Features (31%)</h3>
                <p>Master Individual, Business, and Enterprise plans. Hands-on with Copilot Chat, completions, and extensions in your own projects.</p>
              </div>
            </div>
            
            <div className="master-card">
              <div className="master-icon">
                <img src="/images/icons/handson.png" alt="Data Handling" />
              </div>
              <div className="master-content">
                <h3>Domain 3: How Copilot Works & Data (15%)</h3>
                <p>Understand the AI engine, context window, and data privacy. Practice securing your code and maintaining compliance.</p>
              </div>
            </div>
            
            <div className="master-card">
              <div className="master-icon">
                <img src="/images/icons/interactive.svg" alt="Prompt Engineering" />
              </div>
              <div className="master-content">
                <h3>Domain 4: Prompt Crafting (9%)</h3>
                <p>Master prompt engineering with real scenarios. Practice writing effective prompts to get better code suggestions faster.</p>
              </div>
            </div>
            
            <div className="master-card">
              <div className="master-icon">
                <img src="/images/icons/careergrowth.png" alt="Developer Use Cases" />
              </div>
              <div className="master-content">
                <h3>Domain 5: Developer Use Cases (14%)</h3>
                <p>Apply Copilot to your daily work: code generation, refactoring, documentation, debugging. Bring your own scenarios.</p>
              </div>
            </div>
            
            <div className="master-card">
              <div className="master-icon">
                <img src="/images/icons/handson.png" alt="Testing" />
              </div>
              <div className="master-content">
                <h3>Domain 6: Testing with Copilot (9%)</h3>
                <p>Generate unit tests, integration tests, and test cases. Practice TDD and test automation with AI assistance.</p>
              </div>
            </div>
            
            <div className="master-card">
              <div className="master-icon">
                <img src="/images/icons/mentoring.png" alt="Privacy" />
              </div>
              <div className="master-content">
                <h3>Domain 7: Privacy & Context Exclusions (15%)</h3>
                <p>Configure content exclusions, manage telemetry, and implement privacy policies in real enterprise environments.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pricing-section">
        <div className="container">
          <h2>Course <span className="highlight">Pricing</span></h2>
          <div className="pricing-card">
            <div className="pricing-header">
              <div className="pricing-amount">
                <span className="original-price">{pricingOriginalCurrency}{pricingOriginalAmount}</span>
                <span className="current-price">{pricingCurrency}{pricingAmount}</span>
              </div>
              <p className="pricing-save">(Save 50%)</p>
            </div>
            <div className="pricing-features">
              <div>✓ Hands-on exercises for all 7 domains</div>
              <div>✓ Real-world scenario practice</div>
              <div>✓ Practice assessments + exam sandbox</div>
              <div>✓ Official study guide alignment</div>
              <div>✓ Apply to your own projects</div>
              <div>✓ Production-ready skills</div>
            </div>
            <button 
              onClick={() => { gtag_report_conversion(); openPopup('enroll'); }} 
              className="enroll-button"
            >
              Enroll Now
            </button>
            <p className="pricing-note">Hands-on practice. Ready to apply immediately.</p>
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked <span className="highlight">Questions</span></h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button 
                  className="faq-question" 
                  onClick={() => toggleFaq(index)}
                >
                  {faq.question}
                  <span className={`faq-toggle ${openFaq === index ? 'open' : ''}`}>
                    +
                  </span>
                </button>
                <div className={`faq-answer ${openFaq === index ? 'open' : ''}`}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-final">
        <div className="container">
          <h2>Ready to Master GitHub Copilot?</h2>
          <p>Start hands-on practice today. Apply to your projects tomorrow. Get certified next month.</p>
          <div className="cta-buttons">
            <button 
              onClick={() => { gtag_report_conversion(); openPopup('enroll'); }} 
              className="cta-button cta-button-secondary"
            >
              Enroll Now
            </button>
            <button 
              onClick={() => { gtag_report_conversion(); openPopup('curriculum'); }} 
              className="cta-button cta-button-primary"
            >
              View Curriculum
            </button>
          </div>
        </div>
      </section>

      <PopupForm 
        isOpen={isPopupOpen} 
        onClose={closePopup} 
        formType={formType}
        onSubmit={async (formData) => {
          try {
            await fetch(API_ENDPOINTS.LEADS_SUBMIT, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                ...formData,
                source: 'gh300_page',
                interestedIn: formType === 'enroll' ? 'gh300_copilot_certification' : 'gh300_copilot_info'
              })
            });
            closePopup();
            showConfirmation(formType);
          } catch (error) {
            console.error('Error submitting form:', error);
            alert('Failed to submit form. Please try again.');
          }
        }}
      />

      {showScrollUp && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            right: '32px',
            bottom: '40px',
            zIndex: 1300,
            background: '#fff',
            border: '2px solid #DA291C',
            borderRadius: '50%',
            width: '54px',
            height: '54px',
            boxShadow: '0 2px 12px rgba(44,62,80,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'opacity 0.2s',
            opacity: 0.95
          }}
          aria-label="Scroll to top"
        >
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="16" fill="#fff" />
            <path d="M10 18l6-6 6 6" stroke="#DA291C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        </button>
      )}

      {confirmationModal.isOpen && (
        <div className="confirmation-modal">
          <div className="confirmation-content">
            <button className="close-button" onClick={closeConfirmation}>&times;</button>
            <h3>{confirmationModal.title}</h3>
            <p>{confirmationModal.message}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default GH300;
